import {
  getLocalLots,
  getLocalLot,
  saveLocalLots,
  saveLocalLot,
  getLocalRolls,
  saveLocalRolls,
  saveLocalRoll,
  deleteLocalRoll,
  getLocalDispatches,
  saveLocalDispatches,
  saveLocalDispatch,
  addToOfflineQueue,
  getOfflineQueue,
  markQueueItemSynced,
  markQueueItemFailed,
  clearSyncedQueue,
  setLastSyncTime,
  getLastSyncTime,
  setSyncStatus,
  getSyncStatus,
  rollExistsInLot,
} from './offlineDB';
import * as api from './api';

// Check if online
export function isOnline() {
  return navigator.onLine;
}

// Generate temporary ID for offline items
export function generateTempId() {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============ LOTS ============
export async function fetchLots() {
  if (isOnline()) {
    try {
      const response = await api.getLots();
      await saveLocalLots(response.data);
      return { data: response.data, offline: false };
    } catch (error) {
      // Fall back to local
      const localLots = await getLocalLots();
      return { data: localLots, offline: true };
    }
  } else {
    const localLots = await getLocalLots();
    return { data: localLots, offline: true };
  }
}

export async function fetchLot(lotId) {
  if (isOnline()) {
    try {
      const response = await api.getLot(lotId);
      await saveLocalLot(response.data);
      return { data: response.data, offline: false };
    } catch (error) {
      const localLot = await getLocalLot(lotId);
      return { data: localLot, offline: true };
    }
  } else {
    const localLot = await getLocalLot(lotId);
    return { data: localLot, offline: true };
  }
}

export async function createLotOffline(data) {
  if (isOnline()) {
    try {
      const response = await api.createLot(data);
      await saveLocalLot(response.data);
      return { data: response.data, offline: false };
    } catch (error) {
      throw error;
    }
  } else {
    // Create local lot
    const tempLot = {
      id: generateTempId(),
      lot_no: data.lot_no,
      description: data.description,
      total_rolls: 0,
      total_meter: 0,
      dispatched_meter: 0,
      balance_meter: 0,
      dispatch_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _offline: true,
      _timestamp: Date.now(), // For conflict resolution
    };
    
    await saveLocalLot(tempLot);
    await addToOfflineQueue({
      type: 'CREATE_LOT',
      data: data,
      tempId: tempLot.id,
      timestamp: Date.now(),
    });
    
    return { data: tempLot, offline: true, queued: true };
  }
}

// ============ ROLLS / DISPATCHES — OFFLINE WRITE HELPERS REMOVED (CQ-003) ============
//
// The following helpers used to live here:
//   • fetchRolls(lotId, status)
//   • createRollOffline(lotId, data)
//   • updateRollOffline(lotId, rollId, data, adminOverride)
//   • createDispatchOffline(data)
//   • fetchDispatches(params)
//
// They were unused (zero callers across the codebase) and only *appeared* to
// queue writes when offline. Real dispatch/roll pages always call the live API
// directly, so an offline dispatch would fail silently and give users the
// false impression that their entry was queued.
//
// Per approved recommendation, offline write functionality has been removed.
// The offline indicator now says "Working offline — read-only mode" and only
// read-cache functionality is retained.


// ============ LOCAL STATS UPDATE ============
async function updateLocalLotStats(lotId) {
  const lot = await getLocalLot(lotId);
  if (!lot) return;
  
  const rolls = await getLocalRolls(lotId);
  
  lot.total_rolls = rolls.length;
  lot.total_meter = rolls.reduce((sum, r) => sum + r.meter, 0);
  lot.dispatched_meter = rolls
    .filter(r => r.status === 'Dispatched')
    .reduce((sum, r) => sum + r.meter, 0);
  lot.balance_meter = lot.total_meter - lot.dispatched_meter;
  lot.updated_at = new Date().toISOString();
  
  await saveLocalLot(lot);
}

// ============ SYNC WITH CONFLICT RESOLUTION ============
export async function syncOfflineData() {
  const queue = await getOfflineQueue();
  
  if (queue.length === 0) {
    return { synced: 0, failed: 0, conflicts: [] };
  }
  
  if (!isOnline()) {
    return { synced: 0, failed: 0, conflicts: [], offline: true };
  }
  
  await setSyncStatus('syncing');
  
  let synced = 0;
  let failed = 0;
  const conflicts = [];
  const idMapping = {}; // Map temp IDs to real IDs
  
  for (const item of queue) {
    try {
      let result;
      
      switch (item.type) {
        case 'CREATE_LOT':
          result = await api.createLot(item.data);
          idMapping[item.tempId] = result.data.id;
          await saveLocalLot(result.data);
          break;
          
        case 'CREATE_ROLL':
          // Map lot_id if it was a temp ID
          const actualLotId = idMapping[item.lotId] || item.lotId;
          
          // Check for conflict - roll_no might already exist on server
          try {
            result = await api.createRoll(actualLotId, item.data);
            idMapping[item.tempId] = result.data.id;
            await saveLocalRoll(result.data);
          } catch (error) {
            if (error.response?.status === 400 && 
                error.response?.data?.detail?.includes('already exists')) {
              conflicts.push({
                type: 'DUPLICATE_ROLL',
                item: item,
                message: `Roll '${item.data.roll_no}' already exists. Skipping duplicate.`,
                resolution: 'skipped',
              });
              // Remove local duplicate
              await deleteLocalRoll(item.tempId);
              await markQueueItemSynced(item.id);
              continue; // Don't count as failed or synced
            } else {
              throw error;
            }
          }
          break;
          
        case 'UPDATE_ROLL':
          const actualLotIdForUpdate = idMapping[item.lotId] || item.lotId;
          const actualRollId = idMapping[item.rollId] || item.rollId;
          
          // Fetch the current server state to compare timestamps
          try {
            const serverRollsRes = await api.getRolls(actualLotIdForUpdate);
            const serverRoll = serverRollsRes.data.find(r => r.id === actualRollId);
            
            if (serverRoll) {
              const serverTimestamp = new Date(serverRoll.updated_at).getTime();
              const offlineTimestamp = item.timestamp;
              
              // Latest timestamp wins
              if (serverTimestamp > offlineTimestamp) {
                conflicts.push({
                  type: 'UPDATE_CONFLICT',
                  item: item,
                  message: `Roll '${serverRoll.roll_no}' was modified on server. Server version kept.`,
                  resolution: 'server_wins',
                });
                // Update local with server version
                await saveLocalRoll(serverRoll);
                await markQueueItemSynced(item.id);
                continue;
              }
            }
            
            result = await api.updateRoll(actualLotIdForUpdate, actualRollId, item.data, item.adminOverride);
            await saveLocalRoll(result.data);
          } catch (error) {
            throw error;
          }
          break;
          
        case 'CREATE_DISPATCH':
          const dispatchLotId = idMapping[item.data.lot_id] || item.data.lot_id;
          
          // Map roll IDs if they were temp IDs
          const actualRollIds = item.data.roll_ids.map(id => idMapping[id] || id);
          
          try {
            // Check if any rolls were dispatched by someone else while offline
            const serverRollsRes = await api.getRolls(dispatchLotId);
            const serverRolls = serverRollsRes.data;
            
            const alreadyDispatched = [];
            const stillAvailable = [];
            
            for (const rollId of actualRollIds) {
              const serverRoll = serverRolls.find(r => r.id === rollId);
              if (serverRoll && serverRoll.status === 'Dispatched') {
                // Check if it was dispatched by this offline action
                if (serverRoll.dispatch_id !== item.tempId) {
                  alreadyDispatched.push(serverRoll);
                }
              } else if (serverRoll && serverRoll.status === 'Available') {
                stillAvailable.push(rollId);
              }
            }
            
            if (alreadyDispatched.length > 0) {
              conflicts.push({
                type: 'DISPATCH_CONFLICT',
                item: item,
                message: `${alreadyDispatched.length} roll(s) were already dispatched: ${alreadyDispatched.map(r => r.roll_no).join(', ')}. These were skipped.`,
                resolution: 'partial',
                affectedRolls: alreadyDispatched.map(r => r.roll_no),
              });
              
              // If no rolls are available, skip this dispatch entirely
              if (stillAvailable.length === 0) {
                await markQueueItemSynced(item.id);
                continue;
              }
              
              // Otherwise, dispatch only the available rolls
              result = await api.createDispatch({
                lot_id: dispatchLotId,
                roll_ids: stillAvailable,
                party_name: item.data.party_name,
                challan_no: item.data.challan_no,
              });
            } else {
              result = await api.createDispatch({
                lot_id: dispatchLotId,
                roll_ids: actualRollIds,
                party_name: item.data.party_name,
                challan_no: item.data.challan_no,
              });
            }
            
            idMapping[item.tempId] = result.data.id;
            await saveLocalDispatch(result.data);
            
            // Update local rolls with real dispatch ID
            for (const rollId of actualRollIds) {
              const rolls = await getLocalRolls(dispatchLotId);
              const roll = rolls.find(r => r.id === rollId || idMapping[r.id] === rollId);
              if (roll) {
                roll.dispatch_id = result.data.id;
                roll._offline = false;
                roll._dispatchedOffline = false;
                await saveLocalRoll(roll);
              }
            }
          } catch (error) {
            if (error.response?.status === 400 && 
                error.response?.data?.detail?.includes('already dispatched')) {
              conflicts.push({
                type: 'ALREADY_DISPATCHED',
                item: item,
                message: `Some rolls were already dispatched by another user.`,
                resolution: 'warning',
              });
              await markQueueItemSynced(item.id);
              continue;
            } else {
              throw error;
            }
          }
          break;
          
        default:
          console.warn('Unknown queue item type:', item.type);
      }
      
      await markQueueItemSynced(item.id);
      synced++;
      
    } catch (error) {
      console.error('Sync failed for item:', item, error);
      await markQueueItemFailed(item.id, error.message);
      failed++;
      
      // Stop syncing if we hit too many failures
      if (failed >= 3) {
        break;
      }
    }
  }
  
  await clearSyncedQueue();
  await setLastSyncTime();
  await setSyncStatus('idle');
  
  // Refresh data from server after sync
  if (synced > 0 || conflicts.length > 0) {
    await refreshAllData();
  }
  
  return { synced, failed, conflicts };
}

// Refresh all data from server
async function refreshAllData() {
  try {
    const lotsRes = await api.getLots();
    await saveLocalLots(lotsRes.data);
    
    // Refresh rolls for each lot
    for (const lot of lotsRes.data) {
      const rollsRes = await api.getRolls(lot.id);
      await saveLocalRolls(rollsRes.data);
    }
    
    const dispatchesRes = await api.getDispatches();
    await saveLocalDispatches(dispatchesRes.data);
  } catch (error) {
    console.error('Failed to refresh data:', error);
  }
}

// Initial data sync when coming online
export async function initialSync() {
  if (!isOnline()) return;
  
  try {
    await refreshAllData();
    await setLastSyncTime();
  } catch (error) {
    console.error('Initial sync failed:', error);
  }
}

export { getOfflineQueue, getLastSyncTime, getSyncStatus };
